import { useEffect } from 'react';
import { useMcpBridge } from '../mcp/McpBridgeProvider';
import { usePortalAccess } from './portal/usePortalAccess';
import { useResolvedMcpInstanceId } from './portal/useResolvedMcpInstanceId';
import { useInstances } from './useInstances';

/**
 * Adopt the instance the MCP host selected, once it has been translated to an SFDC id.
 *
 * Returns the resolution state rather than a bare boolean: the lookup is a network round
 * trip now, and a caller that only knows "resolved / not resolved" would render the
 * deployment selector for a beat and then hide it.
 */
export function useMcpInstanceId(setSelectedInstanceIds: (ids: string[]) => void) {
  const { mcpInstanceId } = useMcpBridge();
  const { isPortalSource } = usePortalAccess();
  const {
    data: { instances },
  } = useInstances({ enabled: !isPortalSource });
  const { resolvedId: portalResolvedId, isResolving } = useResolvedMcpInstanceId();

  // No portal source (flag off, or flag on with no host configured): plain membership in
  // the guardian list, as before. The ARTESCA UUID case is unresolvable on this path —
  // exactly as it is today — and the selector stays visible.
  const resolvedId = isPortalSource
    ? portalResolvedId
    : mcpInstanceId && instances.includes(mcpInstanceId)
      ? mcpInstanceId
      : null;

  useEffect(() => {
    if (resolvedId !== null) {
      setSelectedInstanceIds([resolvedId]);
    }
  }, [resolvedId, setSelectedInstanceIds]);

  return { resolvedId, isResolving };
}
